import { createAsyncThunk } from '@reduxjs/toolkit';
import * as Sentry from '@sentry/nextjs';
import { API } from 'aws-amplify';
import { createBrief } from '../brief/thunks';
import type { ProjectType } from '../projects/types';
import { migrateClientMonthsToSheets } from './helpers';
import type {
  BackupListItem,
  BulkBriefBackup,
  BulkBriefClient,
  BulkBriefDraft,
  BulkBriefRow,
  CreateBackupPayload,
  CreateBriefsPayload,
  CreateBriefsResult,
  MonthlyOverview,
  OrchestrationStatusResponse,
  OrchestrationStep,
  RestoreBackupPayload,
  StartOrchestrationResponse,
  StopOrchestrationResponse,
} from './types';

// Helper to map language name to code
const getLanguageCode = (languageName: string): string => {
  const mapping: Record<string, string> = {
    'English': 'en',
    'Spanish': 'es',
    'French': 'fr',
    'German': 'de',
    'Italian': 'it',
    'Portuguese': 'pt',
    'Dutch': 'nl',
    'Polish': 'pl',
    'Russian': 'ru',
    'Chinese': 'zh',
    'Japanese': 'ja',
    'Korean': 'ko',
  };
  return mapping[languageName] || 'en';
};

/**
 * Get all clients for the authenticated user
 * Auto-migrates old month-based data to new sheet-based structure
 */
export const getClients = createAsyncThunk<BulkBriefClient[]>(
  'bulkBriefs/getClients',
  async (_, { rejectWithValue }) => {
    try {
      const response = await API.get('brief', '/briefs/bulk-briefs/clients', {});
      const clients = response.clients || [];

      // Auto-migrate old month-based data to new sheet-based structure
      const migratedClients = clients.map((client: BulkBriefClient) =>
        migrateClientMonthsToSheets(client)
      );

      return migratedClients;
    } catch (error) {
      Sentry.captureException(error);
      console.error('Error getting clients:', error);
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);

/**
 * Get specific draft for client/sheet
 * Supports both new sheetId and legacy month (for backward compatibility)
 */
export const getDraft = createAsyncThunk<
  BulkBriefDraft,
  { clientId: string; sheetId: string; page?: number; limit?: number }
>(
  'bulkBriefs/getDraft',
  async ({ clientId, sheetId, page = 0, limit = 800 }, { rejectWithValue }) => {
    try {
      if (!sheetId) {
        throw new Error('sheetId is required');
      }

      console.log('[GET Request] 📤 Requesting:', { clientId, sheetId });

      const response = await API.get(
        'brief',
        `/briefs/bulk-briefs/${clientId}/${sheetId}?page=${page}&limit=${limit}`,
        {}
      );

      console.log('[GET Response] 📥 Received:', {
        requestedSheetId: sheetId,
        returnedSheetId: response.sheetId,
        returnedSheetName: response.sheetName,
        returnedRows: response.rows?.length,
        MATCH: response.sheetId === sheetId ? '✅' : '❌ MISMATCH!'
      });

      return response;
    } catch (error) {
      Sentry.captureException(error);
      console.error('Error getting draft:', error);
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);

/**
 * Save or update draft
 */
export const saveDraft = createAsyncThunk<
  { success: boolean; metadata: any },
  BulkBriefDraft
>(
  'bulkBriefs/saveDraft',
  async (draft, { rejectWithValue }) => {
    try {
      console.log('[SAVE Request] 💾 Saving:', {
        sheetId: draft.sheetId,
        sheetName: draft.sheetName,
        rowCount: draft.rows?.length
      });

      const response = await API.post('brief', '/briefs/bulk-briefs', { body: draft });

      console.log('[SAVE Response] ✅ Saved successfully');

      return response;
    } catch (error) {
      Sentry.captureException(error);
      console.error('Error saving draft:', error);
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);

/**
 * Delete draft
 * Supports both new sheetId and legacy month (for backward compatibility)
 */
export const deleteDraft = createAsyncThunk<
  { success: boolean },
  { clientId: string; sheetId: string }
>(
  'bulkBriefs/deleteDraft',
  async ({ clientId, sheetId }, { rejectWithValue }) => {
    try {
      if (!sheetId) {
        throw new Error('sheetId is required');
      }

      const response = await API.del('brief', `/briefs/bulk-briefs/${clientId}/${sheetId}`, {});
      return response;
    } catch (error) {
      Sentry.captureException(error);
      console.error('Error deleting draft:', error);
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);

/**
 * Get monthly overview for all clients
 */
export const getMonthlyOverview = createAsyncThunk<MonthlyOverview, string>(
  'bulkBriefs/getMonthlyOverview',
  async (month, { rejectWithValue }) => {
    try {
      const response = await API.get('brief', `/briefs/briefs-briefs/overview/${month}`, {});
      return response;
    } catch (error) {
      Sentry.captureException(error);
      console.error('Error getting overview:', error);
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);

/**
 * Queue briefs for creation using SQS (NEW - recommended for bulk operations)
 * This queues briefs to AWS SQS for background processing
 * Supports both new sheetId and legacy month (for backward compatibility)
 */
export const queueBriefCreation = createAsyncThunk<
  { batchJobId: string; totalQueued: number; jobs: any[] },
  { userId: string; cognitoUserName: string; clientId: string; sheetId?: string; month?: string; rows: any[] }
>(
  'bulkBriefs/queueBriefCreation',
  async ({ userId, cognitoUserName, clientId, sheetId, month, rows }, { rejectWithValue }) => {
    try {
      // Use sheetId if provided, otherwise fall back to month
      const identifier = sheetId || month;

      if (!identifier) {
        throw new Error('Either sheetId or month must be provided');
      }

      console.log('[Thunk] Calling bulkBriefQueue API with:', {
        userId,
        cognitoUserName,
        clientId,
        sheetId: identifier,
        rowCount: rows.length
      });

      console.log('[Thunk] About to call API.post...');

      // Debug: Log articleTitle values in payload
      const mappedRows = rows.map(row => ({
        id: row.id,
        keyword: row.keyword,
        projectId: row.projectId,
        project: row.project,
        projectDomain: row.projectDomain,
        location: row.location,
        language: row.language,
        state: row.state || null,
        articleTitle: row.articleTitle || ''
      }));

      console.log('[Thunk DEBUG] Mapped rows with articleTitle:',
        mappedRows.map(r => ({ id: r.id, keyword: r.keyword, articleTitle: r.articleTitle }))
      );

      // Call the queue API endpoint via brief Lambda proxy
      const response = await API.post('brief', '/briefs/bulk-queue', {
        body: {
          userId,
          cognitoUserName,
          clientId,
          month: identifier, // Backend still uses 'month' key, but accepts any sheet identifier
          rows: mappedRows
        }
      }).catch(apiError => {
        console.error('[Thunk] API.post caught error:', apiError);
        throw apiError;
      });

      console.log('[Thunk] Queue API response:', response);
      return response;
    } catch (error) {
      console.error('[Thunk] Full error object:', error);
      console.error('[Thunk] Error response:', error?.response);
      console.error('[Thunk] Error message:', error?.message);
      console.error('[Thunk] Error stack:', error?.stack);

      Sentry.captureException(error);

      // Return detailed error information
      const errorMessage = error?.response?.data?.error || error?.message || 'Unknown error';
      return rejectWithValue({
        message: errorMessage,
        status: error?.response?.status,
        details: error?.response?.data
      });
    }
  }
);

/**
 * Get status of queued briefs
 * Polls DynamoDB for job status to get briefIds as they become available
 */
export const getQueuedBriefStatus = createAsyncThunk<
  { batchJobId: string; jobs: any[]; summary: any },
  { batchJobId: string }
>(
  'bulkBriefs/getQueuedBriefStatus',
  async ({ batchJobId }, { rejectWithValue }) => {
    try {
      // Call the queue status API via brief Lambda proxy
      const response = await API.get('brief', `/briefs/bulk-queue/status/${batchJobId}`, {});
      return response;
    } catch (error) {
      Sentry.captureException(error);
      console.error('Error getting queued brief status:', error);
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);

/**
 * Create briefs from draft rows (LEGACY - for backward compatibility)
 * This is the old sequential bulk creation function
 * Use queueBriefCreation instead for new implementations
 */
export const createBriefsFromDraft = createAsyncThunk<
  CreateBriefsResult[],
  CreateBriefsPayload & { sheetId?: string }
>(
  'bulkBriefs/createBriefsFromDraft',
  async ({ rows, delayMs = 10000, clientId, month, sheetId }, { dispatch, rejectWithValue }) => {
    const results: CreateBriefsResult[] = [];

    try {
      for (let i = 0; i < rows.length; i++) {
        const row = rows[i];

        // Update progress in UI (dispatch action to update current row)
        dispatch({
          type: 'bulkBriefs/updateCreationProgress',
          payload: {
            total: rows.length,
            completed: i,
            current: row.keyword,
          },
        });

        try {
          // Set individual createdAt timestamp right before API call
          // This ensures timeout is calculated from when THIS brief actually starts
          dispatch({
            type: 'bulkBriefs/updateRowStatus',
            payload: {
              rowId: row.id,
              status: 'creating',
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            },
          });

          // Use state location if US state is selected, otherwise use country
          // Format: "StateName,United States" when state is selected
          console.log("row.state", row?.state)


          const locationName = (row?.state?.trim() && row.location === 'United States')
            ? `${row.state},United States`
            : row.location;
          console.log("location", locationName)
          // Call existing createBrief thunk
          // Pass Yjs context (clientId, sheetId, rowId) for real-time updates
          const result: any = await dispatch(
            createBrief({
              keyword: row.keyword,
              projectId: row.projectId,
              projectName: row.project,
              locationName: locationName,
              languageCode: getLanguageCode(row.language),
              languageName: row.language,
              isOnboardingBrief: false,
              generateArticle: false,
              GSCsiteUrl: '',
              companyDetails: true,
              stats: false,
              insights: '',
              generateAutomatedInsights: true,
              interlinking: false,
              humanize: false,
              seoMissionsBriefId: '',
              thoughtLeadership: false,
              aiResearchAgent: true,
              aiEvalRefineAgent: true,
              articleTitle: row.articleTitle || '',
              // Yjs context for real-time updates when brief completes
              clientId: clientId,
              sheetId: sheetId || month,
              rowId: row.id,
            })
          ).unwrap();

          // Success - save result
          results.push({
            rowId: row.id,
            success: true,
            briefId: result.id,
          });

          // Update row with briefId IMMEDIATELY after API success
          // This ensures briefId is persisted even if page reloads
          console.log(`[Thunk] Brief created successfully - briefId: ${result.id}, rowId: ${row.id}, keyword: ${row.keyword}`);
          dispatch({
            type: 'bulkBriefs/updateRowStatus',
            payload: {
              rowId: row.id,
              status: 'creating',
              briefId: result.id,
              stepRemains: 8,
            },
          });
          console.log(`[Thunk] Dispatched briefId to Redux - polling will start automatically`);

          // Delay before next request (except for last one)
          if (delayMs > 0 && i < rows.length - 1) {
            await new Promise((resolve) => setTimeout(resolve, delayMs));
          }
        } catch (error) {
          // Error creating this brief
          console.error(`Error creating brief for "${row.keyword}":`, error);

          results.push({
            rowId: row.id,
            success: false,
            error: error.message || 'Unknown error',
          });

          // Update row status to error
          dispatch({
            type: 'bulkBriefs/updateRowStatus',
            payload: {
              rowId: row.id,
              status: 'error',
              errorMessage: error.message || 'Failed to create brief',
            },
          });

          // Continue with next brief even if this one failed
        }
      }

      // Auto-save draft after all briefs are created
      // This will be done by the component listening to state changes

      return results;
    } catch (error) {
      Sentry.captureException(error);
      console.error('Fatal error in bulk creation:', error);
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Fetch existing brief data by briefId and prepare it for adding to sheet
 */
export const fetchExistingBriefData = createAsyncThunk<
  {
    briefId: string;
    keyword: string;
    projectId: string;
    projectName: string;
    projectDomain: string;
    location: string;
    language: string;
    state: string | null;
  },
  { briefId: string; projects: ProjectType[] }
>(
  'bulkBriefs/fetchExistingBriefData',
  async ({ briefId, projects }, { rejectWithValue }) => {
    try {
      // Fetch brief data from the API
      const response = await API.get('brief', `/briefs/object/${briefId}`, {});

      if (!response) {
        throw new Error('Brief not found');
      }

      // Response structure is { data: briefObject }, so we need response.data
      const briefData = response.data || response;

      // Log the full brief data for debugging
      console.log('[fetchExistingBriefData] Full brief data:', briefData);

      // Extract and validate required fields
      const keyword = briefData.keyword || '';
      const projectId = briefData.projectId || briefData.jobId || '';

      // Find the project in the user's projects array to get projectDomain
      const matchedProject = projects.find(p => p.projectId === projectId);

      if (!matchedProject) {
        console.error('[fetchExistingBriefData] Project not found in user projects:', projectId);
        throw new Error(`Project not found. You may not have access to this project (ID: ${projectId.substring(0, 8)}...)`);
      }

      const projectName = matchedProject.projectName;
      const projectDomain = matchedProject.projectDomain;

      // Parse location - if it's in "State,Country" format, split it
      let location = briefData.locationName || 'United States';
      let state: string | null = null;

      if (location && location.includes(',')) {
        const parts = location.split(',');
        state = parts[0].trim();
        location = parts[1].trim();
      }

      // Map language code to name if needed
      const languageCode = briefData.languageCode || 'en';
      const languageMapping: Record<string, string> = {
        'en': 'English',
        'es': 'Spanish',
        'fr': 'French',
        'de': 'German',
        'it': 'Italian',
        'pt': 'Portuguese',
        'nl': 'Dutch',
        'pl': 'Polish',
        'ru': 'Russian',
        'zh': 'Chinese',
        'ja': 'Japanese',
        'ko': 'Korean',
      };
      const language = briefData.languageName || languageMapping[languageCode] || 'English';

      // Log extracted data for debugging
      console.log('[fetchExistingBriefData] Extracted data:', {
        keyword,
        projectId,
        projectName,
        projectDomain,
        location,
        language,
        state,
      });

      // Validate that we have minimum required data
      if (!keyword) {
        console.error('[fetchExistingBriefData] Missing keyword in brief data');
        throw new Error('Brief is missing keyword');
      }
      if (!projectId) {
        console.error('[fetchExistingBriefData] Missing projectId in brief data');
        throw new Error('Brief is missing project information');
      }

      return {
        briefId: briefData.id || briefId, // Use the id from response, fallback to input briefId
        keyword,
        projectId,
        projectName,
        projectDomain,
        location,
        language,
        state,
      };
    } catch (error) {
      Sentry.captureException(error);
      console.error('[fetchExistingBriefData] Error:', error);
      console.error('[fetchExistingBriefData] Error response:', error?.response);

      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to fetch brief data';
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Queue briefs for improvement using improve-brief-queue
 * Sends eligible briefs to the improvement queue for processing
 */
export const queueBriefImprovement = createAsyncThunk<
  { batchJobId: string; totalQueued: number; jobs: any[] },
  { userId: string; cognitoUserName: string; clientId: string; sheetId: string; rows: any[] }
>(
  'bulkBriefs/queueBriefImprovement',
  async ({ userId, cognitoUserName, clientId, sheetId, rows }, { rejectWithValue }) => {
    try {
      console.log('[Thunk] Calling improveBriefQueue API with:', {
        userId,
        cognitoUserName,
        clientId,
        sheetId,
        rowCount: rows.length
      });

      // Call the improve-brief-queue API endpoint via brief Lambda proxy
      const response = await API.post('brief', '/briefs/improve-brief-queue', {
        body: {
          userId,
          cognitoUserName,
          clientId,
          sheetId,
          rows: rows.map(row => ({
            rowId: row.id,  // Backend expects rowId, not id
            briefId: row.briefId,
            keyword: row.keyword,
            additionalKeywords: row.additionalKeywords || [],  // Additional keywords from sheet
            articleTitle: row.articleTitle,
            contentType: row.contentType,
            articleInstructions: row.articleInstructions,
            wordcount: row.wordcount,
            internalLinks: row.internalLinks,
            researchLinks: row.researchLinks,
            projectId: row.projectId,
            location: row.location,
            language: row.language
          }))
        }
      });

      console.log('[Thunk] Improve queue API response:', response);
      return response;
    } catch (error) {
      console.error('[Thunk] Full error object:', error);
      console.error('[Thunk] Error response:', error?.response);
      console.error('[Thunk] Error message:', error?.message);

      Sentry.captureException(error);

      const errorMessage = error?.response?.data?.error || error?.message || 'Unknown error';
      return rejectWithValue({
        message: errorMessage,
        status: error?.response?.status,
        details: error?.response?.data
      });
    }
  }
);

/**
 * Get status of improvement queue jobs
 * Polls DynamoDB for job status to track improvement progress
 */
export const getImprovementQueueStatus = createAsyncThunk<
  { batchJobId: string; jobs: any[]; summary: any },
  { batchJobId: string }
>(
  'bulkBriefs/getImprovementQueueStatus',
  async ({ batchJobId }, { rejectWithValue }) => {
    try {
      // Call the improve-brief-queue status API via brief Lambda proxy
      const response = await API.get('brief', `/briefs/improve-brief-queue/status/${batchJobId}`, {});
      // Normalize response: API returns 'statusCounts' but frontend expects 'summary'
      return {
        ...response,
        summary: response.summary || response.statusCounts
      };
    } catch (error) {
      Sentry.captureException(error);
      console.error('Error getting improvement queue status:', error);
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);

export const getHumanizeAgainQueueStatus = createAsyncThunk<
  { batchJobId: string; jobs: any[]; summary: any },
  { batchJobId: string }
>(
  'bulkBriefs/getHumanizeAgainQueueStatus',
  async ({ batchJobId }, { rejectWithValue }) => {
    try {
      // Call the humanize-again-queue status API via brief Lambda proxy
      const response = await API.get('brief', `/briefs/humanize-again-queue/status/${batchJobId}`, {});
      // Normalize response: API returns 'statusCounts' but frontend expects 'summary'
      return {
        ...response,
        summary: response.summary || response.statusCounts
      };
    } catch (error) {
      Sentry.captureException(error);
      console.error('Error getting humanize again queue status:', error);
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);

/**
 * Queue articles for revision using revise-article-queue
 * Uses custom instructions from brief.insights to revise articles with extended thinking
 */
export const queueReviseArticle = createAsyncThunk<
  { batchJobId: string; totalQueued: number; jobs: any[] },
  { userId: string; cognitoUserName: string; clientId: string; sheetId: string; rows: any[] }
>(
  'bulkBriefs/queueReviseArticle',
  async ({ userId, cognitoUserName, clientId, sheetId, rows }, { rejectWithValue }) => {
    try {
      console.log('[Thunk] Calling reviseArticleQueue API with:', {
        userId,
        cognitoUserName,
        clientId,
        sheetId,
        rowCount: rows.length
      });

      // Call the revise-article-queue API endpoint via brief Lambda proxy
      const response = await API.post('brief', '/briefs/revise-article-queue', {
        body: {
          userId,
          cognitoUserName,
          clientId,
          sheetId,
          rows: rows.map(row => ({
            rowId: row.rowId || row.id,
            briefId: row.briefId,
            articleId: row.articleId || '',
            articleTitle: row.articleTitle || '',
            keyword: row.keyword || ''
          }))
        }
      });

      console.log('[Thunk] Revise article queue API response:', response);
      return response;
    } catch (error) {
      console.error('[Thunk] Full error object:', error);
      console.error('[Thunk] Error response:', error?.response);
      console.error('[Thunk] Error message:', error?.message);

      Sentry.captureException(error);
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);

/**
 * Get status of revise article queue jobs
 * Polls DynamoDB for job status to track revision progress
 */
export const getReviseArticleQueueStatus = createAsyncThunk<
  { batchJobId: string; jobs: any[]; summary: any },
  { batchJobId: string }
>(
  'bulkBriefs/getReviseArticleQueueStatus',
  async ({ batchJobId }, { rejectWithValue }) => {
    try {
      // Call the revise-article-queue status API via brief Lambda proxy
      const response = await API.get('brief', `/briefs/revise-article-queue/status/${batchJobId}`, {});
      // Normalize response: API returns 'statusCounts' but frontend expects 'summary'
      return {
        ...response,
        summary: response.summary || response.statusCounts
      };
    } catch (error) {
      Sentry.captureException(error);
      console.error('Error getting revise article queue status:', error);
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);

/**
 * Create a backup of the current draft
 */
export const createBackup = createAsyncThunk<
  { success: boolean; backupId: string },
  CreateBackupPayload
>(
  'bulkBriefs/createBackup',
  async ({ clientId, sheetId, backupType = 'manual' }, { rejectWithValue }) => {
    try {
      const response = await API.post('brief', '/briefs/bulk-briefs/backup', {
        body: { clientId, sheetId, backupType }
      });
      return response;
    } catch (error) {
      Sentry.captureException(error);
      console.error('Error creating backup:', error);
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);

/**
 * Get list of backups for a sheet
 */
export const getBackups = createAsyncThunk<
  BackupListItem[],
  { clientId: string; sheetId: string }
>(
  'bulkBriefs/getBackups',
  async ({ clientId, sheetId }, { rejectWithValue }) => {
    try {
      const response = await API.get(
        'brief',
        `/briefs/bulk-briefs/backups/${clientId}/${sheetId}`,
        {}
      );
      return response.backups || [];
    } catch (error) {
      Sentry.captureException(error);
      console.error('Error fetching backups:', error);
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);

/**
 * Restore a draft from backup
 */
export const restoreBackup = createAsyncThunk<
  { success: boolean; draft: BulkBriefDraft },
  RestoreBackupPayload
>(
  'bulkBriefs/restoreBackup',
  async ({ backupId }, { rejectWithValue }) => {
    try {
      const response = await API.post('brief', '/briefs/bulk-briefs/restore', {
        body: { backupId }
      });
      return response;
    } catch (error) {
      Sentry.captureException(error);
      console.error('Error restoring backup:', error);
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);

/**
 * Queue articles for humanize again using humanize-again-queue
 * Sends eligible articles to the humanize queue for re-processing using humanizedContent
 */
export const queueHumanizeAgain = createAsyncThunk<
  { batchJobId: string; totalQueued: number; jobs: any[] },
  { userId: string; cognitoUserName: string; clientId: string; sheetId: string; rows: any[] }
>(
  'bulkBriefs/queueHumanizeAgain',
  async ({ userId, cognitoUserName, clientId, sheetId, rows }, { rejectWithValue }) => {
    try {
      console.log('[Thunk] Calling humanizeAgainQueue API with:', {
        userId,
        cognitoUserName,
        clientId,
        sheetId,
        rowCount: rows.length
      });

      // Call the humanize-again-queue API endpoint via brief Lambda proxy
      const response = await API.post('brief', '/briefs/humanize-again-queue', {
        body: {
          userId,
          cognitoUserName,
          clientId,
          sheetId,
          rows: rows.map(row => ({
            rowId: row.id,
            briefId: row.briefId,
            articleId: row.articleId || '',
            articleTitle: row.articleTitle || '',
            keyword: row.keyword || ''
          }))
        }
      });

      console.log('[Thunk] Humanize again queue API response:', response);
      return response;
    } catch (error) {
      console.error('[Thunk] Full error object:', error);
      console.error('[Thunk] Error response:', error?.response);
      console.error('[Thunk] Error message:', error?.message);

      Sentry.captureException(error);
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);

/**
 * Queue articles for creation using create-article-queue
 * Creates articles from briefs that have briefStatus='created'
 */
export const queueCreateArticle = createAsyncThunk<
  { batchJobId: string; totalQueued: number; jobs: any[] },
  { userId: string; cognitoUserName: string; clientId: string; sheetId: string; rows: any[] }
>(
  'bulkBriefs/queueCreateArticle',
  async ({ userId, cognitoUserName, clientId, sheetId, rows }, { rejectWithValue }) => {
    try {
      console.log('[Thunk] Calling createArticleQueue API with:', {
        userId,
        cognitoUserName,
        clientId,
        sheetId,
        rowCount: rows.length
      });

      // Call the create-article-queue API endpoint via brief Lambda proxy
      const response = await API.post('brief', '/briefs/create-article-queue', {
        body: {
          userId,
          cognitoUserName,
          clientId,
          sheetId,
          rows: rows.map(row => ({
            rowId: row.id,
            briefId: row.briefId,
            projectId: row.projectId || '',
            keyword: row.keyword || '',
            articleTitle: row.articleTitle || '',
            language: row.language || 'English',
            location: row.location || 'United States'
          }))
        }
      });

      console.log('[Thunk] Create article queue API response:', response);
      return response;
    } catch (error) {
      console.error('[Thunk] Full error object:', error);
      console.error('[Thunk] Error response:', error?.response);
      console.error('[Thunk] Error message:', error?.message);

      Sentry.captureException(error);
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);

/**
 * Get status of create article queue jobs
 * Polls DynamoDB for job status to track article creation progress
 */
export const getCreateArticleQueueStatus = createAsyncThunk<
  { batchJobId: string; jobs: any[]; summary: any },
  { batchJobId: string }
>(
  'bulkBriefs/getCreateArticleQueueStatus',
  async ({ batchJobId }, { rejectWithValue }) => {
    try {
      // Call the create-article-queue status API via brief Lambda proxy
      const response = await API.get('brief', `/briefs/create-article-queue/status/${batchJobId}`, {});
      // Normalize response: API returns 'statusCounts' but frontend expects 'summary'
      return {
        ...response,
        summary: response.summary || response.statusCounts
      };
    } catch (error) {
      Sentry.captureException(error);
      console.error('Error getting create article queue status:', error);
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);

/**
 * Queue briefs for landing page creation using create-landing-page-queue
 * Sends eligible briefs (with articleStatus = 'created') to the landing page queue
 */
export const queueCreateLandingPage = createAsyncThunk<
  { batchJobId: string; totalQueued: number; jobs: any[] },
  { userId: string; cognitoUserName: string; clientId: string; sheetId: string; rows: any[] }
>(
  'bulkBriefs/queueCreateLandingPage',
  async ({ userId, cognitoUserName, clientId, sheetId, rows }, { rejectWithValue }) => {
    try {
      console.log('[Thunk] Calling createLandingPageQueue API with:', {
        userId,
        cognitoUserName,
        clientId,
        sheetId,
        rowCount: rows.length
      });

      // Call the create-landing-page-queue API endpoint via brief Lambda proxy
      const response = await API.post('brief', '/briefs/create-landing-page-queue', {
        body: {
          userId,
          cognitoUserName,
          clientId,
          sheetId,
          rows: rows.map(row => ({
            rowId: row.id,
            briefId: row.briefId
          }))
        }
      });

      console.log('[Thunk] Create landing page queue API response:', response);
      return response;
    } catch (error) {
      console.error('[Thunk] Full error object:', error);
      console.error('[Thunk] Error response:', error?.response);
      console.error('[Thunk] Error message:', error?.message);

      Sentry.captureException(error);

      const errorMessage = error?.response?.data?.error || error?.message || 'Unknown error';
      return rejectWithValue({
        message: errorMessage,
        status: error?.response?.status,
        details: error?.response?.data
      });
    }
  }
);

/**
 * Get status of create landing page queue jobs
 * Polls DynamoDB for job status to track landing page creation progress
 */
export const getCreateLandingPageQueueStatus = createAsyncThunk<
  { batchJobId: string; jobs: any[]; summary: any },
  { batchJobId: string }
>(
  'bulkBriefs/getCreateLandingPageQueueStatus',
  async ({ batchJobId }, { rejectWithValue }) => {
    try {
      // Call the create-landing-page-queue status API via brief Lambda proxy
      const response = await API.get('brief', `/briefs/create-landing-page-queue/status/${batchJobId}`, {});
      // Normalize response: API returns 'statusCounts' but frontend expects 'summary'
      return {
        ...response,
        summary: response.summary || response.statusCounts
      };
    } catch (error) {
      Sentry.captureException(error);
      console.error('Error getting create landing page queue status:', error);
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);

/**
 * Queue rows for fact checking using fact-check-queue
 */
export const queueFactCheck = createAsyncThunk<
  { batchJobId: string; totalQueued: number; jobs: any[] },
  { userId: string; cognitoUserName: string; clientId: string; sheetId: string; rows: any[]; clientGuidelines?: string }
>(
  'bulkBriefs/queueFactCheck',
  async ({ userId, cognitoUserName, clientId, sheetId, rows, clientGuidelines }, { rejectWithValue }) => {
    try {
      const response = await API.post('brief', '/briefs/fact-check-queue', {
        body: {
          userId,
          cognitoUserName,
          clientId,
          sheetId,
          rows: rows.map(row => ({
            rowId: row.id,
            briefId: row.briefId,
            articleTitle: row.articleTitle || '',
            articleMarkdown: row.articleContent || '',
            clientUrl: row.projectDomain || '',
            clientGuidelines: clientGuidelines || 'None provided.',
            articleComments: row.articleInstructions || 'None provided.',
          }))
        }
      });
      return response;
    } catch (error) {
      Sentry.captureException(error);
      console.error('Error queueing fact check:', error);
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);

/**
 * Get status of fact-check queue jobs
 */
export const getFactCheckQueueStatus = createAsyncThunk<
  { batchJobId: string; jobs: any[]; summary: any },
  { batchJobId: string }
>(
  'bulkBriefs/getFactCheckQueueStatus',
  async ({ batchJobId }, { rejectWithValue }) => {
    try {
      const response = await API.get('brief', `/briefs/fact-check-queue/status/${batchJobId}`, {});
      return {
        ...response,
        summary: response.summary || response.statusCounts
      };
    } catch (error) {
      Sentry.captureException(error);
      console.error('Error getting fact-check queue status:', error);
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);

// ============================================================================
// FULL SEQUENCE ORCHESTRATION THUNKS
// For the Fargate-based orchestrator that runs all 5 steps automatically
// ============================================================================

/**
 * Start a full sequence orchestration for multiple briefs
 * Runs all 5 steps: Create Brief -> Improve -> Write Article -> Revise -> Humanize
 */
export const startFullSequenceOrchestration = createAsyncThunk<
  StartOrchestrationResponse,
  { userId: string; rows: BulkBriefRow[]; clientId: string; sheetId: string; startFromStep?: OrchestrationStep }
>(
  'bulkBriefs/startFullSequenceOrchestration',
  async ({ userId, rows, clientId, sheetId, startFromStep }, { rejectWithValue }) => {
    try {
      console.log('[Orchestration] Starting full sequence orchestration:', {
        userId,
        clientId,
        sheetId,
        rowCount: rows.length,
        startFromStep: startFromStep || 'create_brief'
      });

      const response = await API.post('brief', '/briefs/full-sequence-orchestrator', {
        body: {
          userId,
          clientId,
          sheetId,
          rows: rows.map(row => ({
            id: row.id,
            briefId: row.briefId,
            keyword: row.keyword,
            projectId: row.projectId,
            project: row.project,
            projectDomain: row.projectDomain,
            location: row.location,
            language: row.language,
            state: row.state,
            articleTitle: row.articleTitle || '',
            additionalKeywords: row.additionalKeywords || [],
            contentType: row.contentType,
            articleInstructions: row.articleInstructions,
            wordcount: row.wordcount,
            internalLinks: row.internalLinks,
            researchLinks: row.researchLinks
          })),
          startFromStep: startFromStep || 'create_brief'
        }
      });

      console.log('[Orchestration] Started successfully:', response);
      return response;
    } catch (error) {
      console.error('[Orchestration] Error starting:', error);
      Sentry.captureException(error);

      const errorMessage = error?.response?.data?.error || error?.message || 'Unknown error';
      return rejectWithValue({
        message: errorMessage,
        status: error?.response?.status,
        details: error?.response?.data
      });
    }
  }
);

/**
 * Get the status of a running orchestration
 * Returns summary, step breakdown, and individual brief statuses
 */
export const getOrchestrationStatus = createAsyncThunk<
  OrchestrationStatusResponse,
  { orchestrationId: string }
>(
  'bulkBriefs/getOrchestrationStatus',
  async ({ orchestrationId }, { rejectWithValue }) => {
    try {
      const response = await API.get(
        'brief',
        `/briefs/full-sequence-orchestrator/${orchestrationId}`,
        {}
      );
      return response;
    } catch (error) {
      Sentry.captureException(error);
      console.error('[Orchestration] Error getting status:', error);
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);

/**
 * Get active orchestrations for the current user
 * Returns any RUNNING or STARTING orchestrations
 */
export const getActiveOrchestrations = createAsyncThunk(
  'bulkBriefs/getActiveOrchestrations',
  async (_, { rejectWithValue }) => {
    try {
      const response = await API.get(
        'brief',
        '/briefs/full-sequence-orchestrator/user/active',
        {}
      );
      return response;
    } catch (error) {
      console.error('[Orchestration] Error getting active orchestrations:', error);
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);

/**
 * Stop a running orchestration
 * Stops the ECS task and marks remaining briefs as not processed
 */
export const stopOrchestration = createAsyncThunk<
  StopOrchestrationResponse,
  { orchestrationId: string }
>(
  'bulkBriefs/stopOrchestration',
  async ({ orchestrationId }, { rejectWithValue }) => {
    try {
      console.log('[Orchestration] Stopping:', orchestrationId);

      const response = await API.post(
        'brief',
        `/briefs/full-sequence-orchestrator/${orchestrationId}/stop`,
        {}
      );

      console.log('[Orchestration] Stopped successfully:', response);
      return response;
    } catch (error) {
      console.error('[Orchestration] Error stopping:', error);
      Sentry.captureException(error);
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);
